import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';

const Clients = () => {
    const clientsRef = useRef(null);
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();

    const clients = [
        "BLOOM MEDIA FRANCE",
        "POUBELLE DU SKI",
        "Andalou Arabesque",
        "Billions",
        "Spectre Export",
        "MEDITERRANEAN EXPORT",
        "PLUS CANADA",
        "Immigration WorldWide"
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.8, y: 30 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    return (
        <div 
            ref={clientsRef}
            className="bg-white/50 dark:bg-black/50 backdrop-blur-md min-h-screen overflow-y-auto flex flex-col relative py-20"
        >
            {/* Vibrant animated background */}
            {!prefersReducedMotion && (() => {
                const orbCount = isMobile || isLowEndDevice ? 2 : 3; // Reduced from 5
                const blurAmount = isMobile || isLowEndDevice ? '35px' : '70px';
                
                return (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {/* Floating geometric shapes - optimized for mobile */}
                        {Array.from({ length: orbCount }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute rounded-full"
                                style={{
                                    width: `${200 + i * 100}px`,
                                    height: `${200 + i * 100}px`,
                                    background: `radial-gradient(circle, ${
                                        i % 3 === 0 
                                            ? 'rgba(34, 197, 94, 0.7)' 
                                            : i % 3 === 1
                                            ? 'rgba(59, 130, 246, 0.7)'
                                            : 'rgba(236, 72, 153, 0.7)'
                                    } 0%, transparent 70%)`,
                                    filter: `blur(${blurAmount})`,
                                    left: `${-8 + i * 20}%`,
                                    top: `${12 + i * 18}%`,
                                    opacity: 0.45,
                                    willChange: 'transform, opacity',
                                    contain: 'layout style paint',
                                    transform: 'translateZ(0)',
                                }}
                                animate={{
                                    x: [0, isMobile ? 40 : 70, isMobile ? -35 : -60, 0],
                                    y: [0, isMobile ? -40 : -70, isMobile ? 35 : 60, 0],
                                    scale: isMobile ? [1, 1.2, 0.85, 1] : [1, 1.5, 0.7, 1],
                                    opacity: [0.45, 0.65, 0.3, 0.45],
                                }}
                                transition={{
                                    duration: 14 + i * 2.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 2,
                                    type: "tween",
                                }}
                            />
                        ))}
                    </div>
                );
            })()}
            <section className="flex-1 flex flex-col justify-center relative z-10">
                <div className="max-w-7xl mx-auto px-4 md:px-12 w-full">
                    <motion.div 
                        className="mb-12"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h2 
                            className="text-center text-4xl md:text-6xl text-gray-900 dark:text-white font-black mb-4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            Nos Clients
                        </motion.h2>
                        <motion.p 
                            className="text-center text-lg md:text-xl font-light text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Parmi ceux qui ont fait appel à nos services
                        </motion.p>
                    </motion.div>

                    <motion.div 
                        className="grid grid-cols-3 gap-6 md:gap-8"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {clients.map((client, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                whileHover={{ scale: 1.05, y: -5 }}
                                className="flex items-center justify-center h-32 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#262626] cursor-pointer"
                                role="img"
                                aria-label={`Logo client ${client}`}
                            >
                                <span className="text-gray-500 dark:text-gray-400 text-sm md:text-base font-medium text-center px-2">
                                    {client}
                                </span>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>
        </div>
    )
}

export default Clients;